import * as React from 'react';
import {
  Input,
  FormPanel,
  SubmitButton,
  Select,
  PhoneNumberInput,
  LivingUnitSelect,
  InputError,
  RadioButtonGroup,
} from '@ixicare/ui';
import RoleExplainer from './RoleExplainer';
import { flattenOrganizations, useOrganizations } from '~/modules/sites/hooks/useOrganizations';
import { t } from '~/i18n';
import { getDefaultStateReducer, getDefaultReducerChange } from '~/utils/reducer';
import { Link, useNavigate } from 'react-router-dom';
import ErrorContainer from '../Error';
import { genders, teams } from '~/config/profile';
import { removeSameValues } from '~/utils/object';
import { getFieldError } from '~/utils/error';
import {
  fetchLivingUnits,
  Language,
  LANGUAGES,
  languageToCode,
  postLivingUnit,
  Profile,
  ProfileType,
  TLivingUnit,
  UserProfileParamaters,
  WatcherrApiError,
  WatcherrError,
} from '@ixicare/resources';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { useStores } from '~store/utils';
import { observer } from 'mobx-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserAction } from '~config/toastNotification';
import { sortBy } from 'lodash';
import { setLanguage } from '~/i18n';

const SITE_ONLY_VALUE = '__site_only__';

const sameTeams = (a?: string[] | null, b?: string[] | null) =>
  [...(a ?? [])].sort().join('\n') === [...(b ?? [])].sort().join('\n');

type ProfileFormField =
  'email' | 'password' | 'healthLevel' | 'nationalNumber' | 'phoneNumbers' | 'livingUnit' | 'birthday';

interface ProfileFormContainerProps {
  profile?: UserProfileEntry;
  fields?: ProfileFormField[];
  urlPath: string;
  storeType: string;
  donePath?: string;
  profileType: ProfileType;
}

interface ProfileFormContainerState {
  firstName: string;
  lastName: string;
  email?: string | null;
  password?: string | null;
  deviceToken?: string | null;
  phoneNumber?: string;
  secondaryPhoneNumbers?: string;
  healthLevel?: string | null;
  gender?: string;
  nationalNumber?: string | null;
  livingUnitId?: string | null;
  teams?: any[] | null;
  language?: Language | null;
  permissionOrganizationId?: string | null;
}

const ProfileFormContainer: React.FC<ProfileFormContainerProps> = ({
  profile,
  fields,
  urlPath,
  donePath,
  profileType,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notifications, auth, sites, permissions } = useStores();

  const updateProfile = useMutation({
    mutationFn: (x: UserProfileParamaters) => Profile.updateProfile(profile?.id ?? -1, x),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', data.id], data);
      if (auth.user?.id === data.id) {
        setLanguage(languageToCode(data.language));
      }
      if (data.profileType === ProfileType.HealthcareProvider) {
        location.reload();
        notifications.addUserActionNotification({
          type: UserAction.ResidentCreate,
          notificationType: 'success',
          message: t('alerts.residents.update.success', {
            firstName: data.firstName,
            lastName: data.lastName,
          }),
        });
      } else {
        navigate(donePath || `/${urlPath}/${data.id}`, { replace: true });
        notifications.addUserActionNotification({
          type: UserAction.ResidentCreate,
          notificationType: 'success',
          message: t('alerts.residents.update.success', {
            firstName: data.firstName,
            lastName: data.lastName,
          }),
        });
      }
      profile?.update(data);
      //location.reload();
    },
  });
  const createProfile = useMutation({
    mutationFn: Profile.createProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', data.id], data);
      navigate(donePath || `/${urlPath}/${data.id}`, { replace: true });
      notifications.addUserActionNotification({
        type: UserAction.ResidentCreate,
        notificationType: 'success',
        message: t(
          data.profileType === ProfileType.Resident
            ? 'alerts.residents.create.success'
            : 'alerts.healthcareProviders.create.success',
          {
            firstName: data.firstName,
            lastName: data.lastName,
          }
        ),
      });
    },
  });

  const error = (updateProfile.error || createProfile.error) as WatcherrError;
  const loading = updateProfile.isPending || createProfile.isPending;

  // On an email collision the backend returns the colliding profile's id when
  // it sits in the caller's scope. Fetch it to learn its type + name so we can
  // link the admin straight to the existing profile to edit instead.
  const collidingProfileId =
    createProfile.error instanceof WatcherrApiError
      ? (createProfile.error.body as { existingProfileId?: number } | undefined)?.existingProfileId
      : undefined;
  const collidingProfile = useQuery({
    queryKey: ['profile', collidingProfileId],
    queryFn: () => Profile.getProfile(collidingProfileId as number),
    enabled: collidingProfileId != null,
  });
  const collidingProfilePath = collidingProfile.data
    ? collidingProfile.data.profileType === ProfileType.Resident
      ? `/residents/${collidingProfile.data.id}/edit`
      : `/healthcare-providers/${collidingProfile.data.id}/edit`
    : undefined;

  const authenticatedUser = auth.user;
  const isUpdate = React.useMemo(() => !!profile, [profile]);
  // block updates for non-residents during impersonation for security reasons
  const blockedByImpersonation = auth.isImpersonating && !(isUpdate && profileType === ProfileType.Resident);
  const hasSitePermission = permissions.has('can_view_site');
  const siteId = profile?.site?.uuid ?? (hasSitePermission ? sites.selectedSite?.uuid : authenticatedUser?.site?.uuid);
  const initialTeams = React.useMemo(() => {
    const profileTeamNames = (profile?.teams ?? []).map((t) => t.name);
    return (
      [...teams]
        .sort((a, b) => b.value.length - a.value.length)
        .find((option) => option.value.every((tm) => profileTeamNames.includes(tm)))?.value ?? null
    );
  }, [profile]);
  const [state, dispatch] = React.useReducer(getDefaultStateReducer<ProfileFormContainerState>(), {
    firstName: (profile && profile.firstName) || '',
    lastName: (profile && profile.lastName) || '',
    email: profile && profile.email,
    password: '',
    healthLevel: profile?.healthLevel ?? profile?.katzLevel?.toString() ?? '',
    phoneNumber: profile?.phoneNumber,
    secondaryPhoneNumbers: profile?.secondaryPhoneNumbers,
    gender: (profile && profile.gender) || '',
    nationalNumber: (profile && profile.nationalNumber) || null,
    livingUnitId: profile?.livingUnitId || null,
    birthday: profile?.birthday?.toISOString().slice(0, 10) || null,
    teams: initialTeams,
    language: profile?.language ?? null,
    permissionOrganizationId: profile?.permissionOrganizationId ?? null,
  });

  const [passwordMode, setPasswordMode] = React.useState<'invite' | 'manual'>('invite');
  const passwordModeOptions = React.useMemo(
    () => [
      { value: 'invite', label: t('profile.create.passwordOptionInvite') },
      { value: 'manual', label: t('profile.create.passwordOptionManual') },
    ],
    []
  );

  const organizationsQuery = useOrganizations();
  const scopeRootId = auth.user?.permissionOrganizationId ?? null;
  const organizationOptions = React.useMemo(() => {
    const flat = flattenOrganizations(organizationsQuery.data ?? []);
    return [
      { value: SITE_ONLY_VALUE, label: t('profile.create.allowedOrganizationSiteOnly') },
      ...flat.map(({ node, depth }) => ({
        value: node.id,
        label:
          node.id === scopeRootId
            ? `${node.name} ${t('profile.create.allowedOrganizationEntireSuffix')}`
            : `${'  '.repeat(depth)}${node.name}`,
      })),
    ];
  }, [organizationsQuery.data, scopeRootId]);
  const handlePermissionOrganizationChange = React.useCallback((value) => {
    dispatch({ value: value === SITE_ONLY_VALUE ? null : (value ?? null), field: 'permissionOrganizationId' });
  }, []);
  const handleChange = getDefaultReducerChange<ProfileFormContainerState>(dispatch);
  const handleGenderChange = React.useCallback((value) => {
    dispatch({ value, field: 'gender' });
  }, []);
  const handleTeamsChange = React.useCallback((value) => {
    dispatch({ value, field: 'teams' });
  }, []);
  const handleLivingUnitChange = React.useCallback((value) => {
    dispatch({ value, field: 'livingUnitId' });
  }, []);
  const handleLanguageChange = React.useCallback((value) => {
    dispatch({ value: value ?? null, field: 'language' });
  }, []);

  const languageOptions = React.useMemo(
    () =>
      sortBy(
        LANGUAGES.map((l) => ({ value: l.language, label: l.nativeName })),
        'label'
      ),
    []
  );
  const handlePhoneNumberChange = React.useCallback((value) => {
    dispatch({ value: value ?? null, field: 'phoneNumber' });
  }, []);
  const handleSecondaryPhoneNumberChange = React.useCallback((value) => {
    dispatch({ value: value ?? null, field: 'secondaryPhoneNumbers' });
  }, []);

  const installerTeamSelected = Array.isArray(state.teams) && state.teams.includes('Installer Team');
  const showOrganizationScope = profileType === ProfileType.HealthcareProvider && installerTeamSelected;

  const livingUnitField = fields?.includes('livingUnit');
  const livingUnits = useQuery({
    queryKey: ['livingUnits', siteId],
    queryFn: () => fetchLivingUnits({ siteId }),
    enabled: livingUnitField,
  });

  const options = React.useMemo(() => {
    return (
      sortBy(
        livingUnits.data?.map((x) => ({
          label: x.name,
          value: x.id,
        })),
        'label'
      ) ?? []
    );
  }, [livingUnits.data]);

  const createLuMutation = useMutation({ mutationFn: postLivingUnit });

  const teamOptions = React.useMemo(
    () => teams.filter((option) => option.value.every((tm) => authenticatedUser?.teams?.some((t) => t.name === tm))),
    [authenticatedUser]
  );

  const handleCreateOption = async (luName: string) => {
    if (!siteId) {
      return;
    }
    const resp = await createLuMutation.mutateAsync({ siteId, name: luName });
    queryClient.setQueryData<TLivingUnit[]>(['livingUnits', siteId], (data) => (data ?? [])?.concat(resp));
    livingUnits.refetch();
    handleLivingUnitChange(resp.id);
  };

  const handleSubmit = React.useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      const results = removeSameValues(state, profile);
      const nationalNumber = results.nationalNumber == '' ? null : results.nationalNumber;
      const gender = results.gender == '' ? null : results.gender;
      let body: UserProfileParamaters;
      if (profileType === ProfileType.Resident) {
        body = {
          first_name: results.firstName,
          last_name: results.lastName,
          email: results.email,
          health_level: results.healthLevel,
          gender,
          national_number: nationalNumber,
          living_unit_id: results.livingUnitId,
          phone_number: results.phoneNumber,
          secondary_phone_numbers: results.secondaryPhoneNumbers,
          birthday: results.birthday ? new Date(results.birthday).toISOString() : undefined,
        };
      } else {
        body = {
          first_name: results.firstName,
          last_name: results.lastName,
          email: results.email,
          health_level: results.healthLevel,
          gender,
          national_number: nationalNumber,
          living_unit_id: results.livingUnitId,
          phone_number: results.phoneNumber,
          secondary_phone_numbers: results.secondaryPhoneNumbers,
          birthday: results.birthday ? new Date(results.birthday).toISOString() : undefined,
          // Teams are arrays, so removeSameValues always sees them as changed. Only send them when
          // the selection moved: without can_edit_all_profiles any teams in the body is rejected.
          ...(sameTeams(state.teams, initialTeams) ? {} : { teams: results.teams }),
          ...('language' in results ? { language: results.language ?? null } : {}),
        };
      }

      const organizationScopePayload = (() => {
        if (!showOrganizationScope) return {};
        const newValue = state.permissionOrganizationId ?? null;
        const oldValue = profile?.permissionOrganizationId ?? null;
        if (newValue === oldValue) return {};
        return { permission_organization_id: newValue };
      })();

      if (profile) {
        updateProfile.mutate({
          ...body,
          ...organizationScopePayload,
          ...(state.password?.length ? { password: state.password } : {}),
        });
      } else {
        createProfile.mutate({
          ...body,
          ...organizationScopePayload,
          profile_type: installerTeamSelected ? ProfileType.Installer : profileType,
          ...(hasSitePermission && sites.selectedSite ? { site_id: sites.selectedSite.id } : {}),
          ...(passwordMode === 'manual' && state.password ? { password: state.password } : {}),
        });
      }
    },
    [state, showOrganizationScope, passwordMode, initialTeams]
  );

  const phoneFields = fields?.includes('phoneNumbers') ? (
    <>
      <div className="col-6 mb-30">
        <PhoneNumberInput
          name="phoneNumber"
          onValueChange={handlePhoneNumberChange}
          error={getFieldError('phoneNumber', error)}
          value={state.phoneNumber ?? ''}
          label={t('alert.call.residentPrimaryPhoneNumber')}
        />
      </div>
      <div className="col-6 mb-30">
        <PhoneNumberInput
          name="secondaryPhoneNumbers"
          onValueChange={handleSecondaryPhoneNumberChange}
          error={getFieldError('secondaryPhoneNumbers', error)}
          value={state.secondaryPhoneNumbers ?? ''}
          label={t('alert.call.residentSecondaryPhoneNumber')}
        />
      </div>
    </>
  ) : null;

  return (
    <>
      <ErrorContainer error={error} />
      {collidingProfilePath && (
        <p className="mb-15">
          <Link to={collidingProfilePath}>
            {t('profile.create.emailExistsGoToProfile')}
            {collidingProfile.data && ` (${collidingProfile.data.firstName} ${collidingProfile.data.lastName})`}
          </Link>
        </p>
      )}
      <FormPanel>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6  mb-30">
              <Input
                name="firstName"
                onChange={handleChange}
                value={state.firstName}
                error={getFieldError('firstName', error)}
                required
                label={t('profile.create.firstName')}
              />
            </div>
            <div className="col-12 col-md-6 mb-30">
              <Input
                name="lastName"
                onChange={handleChange}
                required
                error={getFieldError('lastName', error)}
                value={state.lastName}
                label={t('profile.create.lastName')}
              />
            </div>

            {fields?.includes('email') && (
              <div className="col-12 mb-30">
                <Input
                  name="email"
                  required
                  onChange={handleChange}
                  error={getFieldError('email', error)}
                  value={state.email ?? ''}
                  label={t('profile.create.email')}
                />
              </div>
            )}
            {fields?.includes('password') && !isUpdate && (
              <div className="col-12 mb-30">
                <RadioButtonGroup
                  name="passwordMode"
                  value={passwordMode}
                  onChange={(value) => setPasswordMode(value)}
                  options={passwordModeOptions}
                />
                {passwordMode === 'manual' && (
                  <Input
                    name="password"
                    type="password"
                    onChange={handleChange}
                    required
                    value={state.password ?? ''}
                    error={getFieldError('password', error)}
                    label={t('profile.create.password')}
                  />
                )}
              </div>
            )}
            {fields?.includes('password') && isUpdate && (
              <div className="col-12 mb-30">
                <Input
                  name="password"
                  type="password"
                  onChange={handleChange}
                  value={state.password ?? ''}
                  error={getFieldError('password', error)}
                  label={t('profile.create.password')}
                />
                <p className="text-muted mb-0 mt-5">{t('profile.update.passwordKeepHint')}</p>
              </div>
            )}

            {fields?.includes('healthLevel') && (
              <div className="col-6 mb-30">
                <Input
                  name="healthLevel"
                  type="text"
                  onChange={handleChange}
                  value={state.healthLevel ?? ''}
                  error={getFieldError('healthLevel', error)}
                  label={t('profile.healthLevel')}
                />
              </div>
            )}
            {livingUnitField && (
              <div className="col-6 mb-30">
                {siteId && (
                  <LivingUnitSelect
                    options={options}
                    onChange={handleLivingUnitChange}
                    onCreateOption={handleCreateOption}
                    livingUnitId={state.livingUnitId ?? undefined}
                    isLoading={livingUnits.isLoading || createLuMutation.isPending}
                  />
                )}
                <InputError error={getFieldError('livingUnitId', error)} />
              </div>
            )}
            <div className="col-6 mb-30">
              <Select
                value={state.gender}
                onChange={handleGenderChange}
                label={t('profile.create.gender')}
                placeholder={t('profile.create.genderPlaceholder')}
                options={genders}
              />
              <InputError error={getFieldError('gender', error)} />
            </div>
            {profileType !== ProfileType.Resident && (
              <div className="col-6 mb-30">
                <Select
                  value={state.language}
                  onChange={handleLanguageChange}
                  label={t('global.language')}
                  placeholder={t('global.languagePlaceholder')}
                  options={languageOptions}
                />
                <InputError error={getFieldError('language', error)} />
              </div>
            )}
            {profileType !== ProfileType.Resident && phoneFields}
            {profileType === ProfileType.HealthcareProvider && (
              <>
                <div className="col-12 col-md-6 mb-30">
                  <Select
                    value={state.teams}
                    onChange={handleTeamsChange}
                    label={t('profile.create.team')}
                    options={teamOptions}
                    required
                  />
                  <InputError error={getFieldError('teams', error)} />
                </div>
                <div className="col-12 col-md-6 mb-30 mt-30">
                  <RoleExplainer items={teamOptions} />
                </div>
              </>
            )}
            {showOrganizationScope &&
              (() => {
                const currentScopeId = profile?.permissionOrganizationId ?? null;
                const currentScopeOutOfReach =
                  currentScopeId !== null && !organizationOptions.some((o) => o.value === currentScopeId);
                if (currentScopeOutOfReach) {
                  return (
                    <div className="col-12 mb-30">
                      <label className="form-label d-block mb-10">{t('global.hasAccessTo')}</label>
                      <p className="text-warning mb-0">{t('profile.create.allowedOrganizationOutOfReach')}</p>
                    </div>
                  );
                }
                return (
                  <div className="col-12 mb-30">
                    <Select
                      value={state.permissionOrganizationId ?? SITE_ONLY_VALUE}
                      onChange={handlePermissionOrganizationChange}
                      label={t('global.hasAccessTo')}
                      options={organizationOptions}
                    />
                    <InputError error={getFieldError('permissionOrganizationId', error)} />
                  </div>
                );
              })()}
            {fields?.includes('birthday') && (
              <div className="col-6 mb-30">
                <Input
                  type="date"
                  name="birthday"
                  onChange={handleChange}
                  error={getFieldError('birthday', error)}
                  value={state.birthday ?? ''}
                  label={t('profile.create.birthday')}
                />
              </div>
            )}
            {profileType === ProfileType.Resident && phoneFields}
            {fields?.includes('nationalNumber') && (
              <div className="col-6 mb-30">
                <Input
                  name="nationalNumber"
                  onChange={handleChange}
                  error={getFieldError('nationalNumber', error)}
                  value={state.nationalNumber ?? ''}
                  label={t('profile.create.nationalNumber')}
                />
              </div>
            )}
          </div>
          {blockedByImpersonation && (
            <p className="text-warning mb-15" title={t('impersonation.disabled.tooltip')}>
              {t('impersonation.disabled.formNotice')}
            </p>
          )}
          <SubmitButton loading={loading} disabled={blockedByImpersonation}>
            {profile ? t('profile.update.submit') : t('profile.create.submit')}
          </SubmitButton>
        </form>
      </FormPanel>
    </>
  );
};
export default observer(ProfileFormContainer);
